import React from 'react'

type LoadingSkeletonVariant =
    | 'default'
    | 'circle-avatar'
    | 'entity-avatar'
    | 'issue-card'
    | 'entity-card'
    | 'comment-card'
    | 'settings'
    | 'actor-card'
    | 'graph'
    | 'graph-card'

interface LoadingSkeletonProps {
    count?: number
    height?: string
    width?: string
    margin?: string
    padding?: string
    avatarSize?: string
    className?: string
    containerClassName?: string
    backgroundColor?: string
    rounded?: string
    variant?: LoadingSkeletonVariant
    /**
     * Localized accessible name announced to assistive tech when the skeleton
     * represents a live loading state. Required for the `graph` and
     * `graph-card` variants so the screen reader hears a polite status
     * message instead of a silent visual.
     */
    ariaLabel?: string
}

const baseSkeletonClassName =
    'relative animate-pulse bg-eoe-input dark:bg-eoe-dark-input'

/* ───────── Graph canvas skeleton (shared by `graph` and `graph-card`) ───────── */

/**
 * ViewBox for the simplified graph skeleton. The aspect is intentionally wider
 * than tall so the shape feels like the real React Flow viewport on both the
 * full-page explorer and the smaller feed-card canvas.
 */
const SKELETON_GRAPH_VIEWBOX = { width: 480, height: 300 }

/**
 * Pixel-position of every placeholder node inside the graph SVG. We render a
 * small, deliberately-pretty cluster of 7 nodes arranged in 4 bands (hero at
 * top, two children, three grand-children, one leaf) so the result reads as a
 * recognisable influence graph — but much simpler than the real canvas.
 */
const SKELETON_GRAPH_NODES: ReadonlyArray<{
    x: number
    y: number
    w: number
    h: number
    hero?: boolean
}> = [
    { x: 180, y: 22, w: 120, h: 42, hero: true },
    { x: 70, y: 100, w: 120, h: 36 },
    { x: 300, y: 100, w: 120, h: 36 },
    { x: 10, y: 175, w: 120, h: 36 },
    { x: 180, y: 175, w: 120, h: 36 },
    { x: 355, y: 175, w: 120, h: 36 },
    { x: 180, y: 250, w: 120, h: 36 },
]

/**
 * SVG path definitions for the skeleton edges. Each path is a smoothstep-style
 * cubic bezier from the bottom-center of a source node to the top-center of a
 * target node so the curve reads like a real React Flow edge rather than a
 * straight line.
 */
const SKELETON_GRAPH_EDGES: ReadonlyArray<string> = [
    'M 240 64 C 240 82 130 82 130 100',
    'M 240 64 C 240 82 360 82 360 100',
    'M 130 136 C 130 155 70 155 70 175',
    'M 130 136 C 130 155 240 155 240 175',
    'M 360 136 C 360 155 240 155 240 175',
    'M 360 136 C 360 155 415 155 415 175',
    'M 240 211 L 240 250',
]

interface GraphCanvasSkeletonProps {
    /** Optional localized polite status announcement. */
    ariaLabel?: string
    /** Extra classes applied to the outermost wrapper (e.g. to set min height). */
    className?: string
}

/**
 * Pretty, simplified placeholder for the React Flow graph canvas. Renders a
 * subtle dot grid + 7 rounded "node cards" connected with smoothstep edges,
 * each block pulsing on a small stagger so the whole cluster feels alive
 * rather than statically dimmed.
 *
 * Exported so feature surfaces (e.g. graph collection cards) can drop it in
 * wherever the live `GraphCanvas` eventually renders without depending on the
 * `variant` string API of `LoadingSkeleton` itself.
 */
export const GraphCanvasSkeleton: React.FC<GraphCanvasSkeletonProps> = ({
    ariaLabel,
    className = '',
}) => {
    const statusAttrs = ariaLabel
        ? {
              role: 'status' as const,
              'aria-label': ariaLabel,
              'aria-live': 'polite' as const,
              'aria-busy': true as const,
          }
        : { 'aria-hidden': true as const }

    return (
        <div
            {...statusAttrs}
            className={`loading-skeleton-graph-canvas relative h-full min-h-[260px] w-full overflow-hidden bg-eoe-bg dark:bg-eoe-dark-bg ${className}`}
        >
            {/* Subtle dot-grid background mirroring the real React Flow Background. */}
            <svg
                aria-hidden='true'
                className='loading-skeleton-graph-canvas-grid absolute inset-0 h-full w-full text-eoe-border-main/40 dark:text-eoe-dark-border-main/40'
            >
                <defs>
                    <pattern
                        id='loading-skeleton-graph-dots'
                        x='0'
                        y='0'
                        width='16'
                        height='16'
                        patternUnits='userSpaceOnUse'
                    >
                        <circle cx='1' cy='1' r='1' fill='currentColor' />
                    </pattern>
                </defs>
                <rect width='100%' height='100%' fill='url(#loading-skeleton-graph-dots)' />
            </svg>

            {/* Foreground graph: edges first so the node cards paint over them. */}
            <svg
                aria-hidden='true'
                viewBox={`0 0 ${SKELETON_GRAPH_VIEWBOX.width} ${SKELETON_GRAPH_VIEWBOX.height}`}
                preserveAspectRatio='xMidYMid meet'
                className='loading-skeleton-graph-canvas-svg relative h-full w-full'
            >
                <g
                    className='loading-skeleton-graph-canvas-edges text-eoe-border-main dark:text-eoe-dark-border-main'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    fill='none'
                    strokeLinecap='round'
                >
                    {SKELETON_GRAPH_EDGES.map((d, index) => (
                        <path
                            key={`loading-skeleton-graph-edge-${index}`}
                            d={d}
                            opacity='0.55'
                            className='animate-pulse'
                            style={{ animationDelay: `${(index % 4) * 140}ms` }}
                        />
                    ))}
                </g>
                {SKELETON_GRAPH_NODES.map((node, index) => {
                    const cx = node.x + 16
                    const cy = node.y + node.h / 2
                    const textX = node.x + 30
                    const titleWidth = node.w - 44
                    return (
                        <g
                            key={`loading-skeleton-graph-node-${index}`}
                            className='loading-skeleton-graph-canvas-node animate-pulse'
                            style={{ animationDelay: `${index * 90}ms` }}
                        >
                            <rect
                                x={node.x}
                                y={node.y}
                                width={node.w}
                                height={node.h}
                                rx='10'
                                ry='10'
                                className='fill-eoe-bg stroke-eoe-border-main dark:fill-eoe-dark-bg-modal dark:stroke-eoe-dark-border-main'
                                strokeWidth='1'
                            />
                            <circle
                                cx={cx}
                                cy={cy}
                                r={node.hero ? 10 : 9}
                                className='fill-eoe-input dark:fill-eoe-dark-input'
                            />
                            <rect
                                x={textX}
                                y={cy - 7}
                                width={titleWidth}
                                height={node.hero ? 6 : 5}
                                rx='2.5'
                                className='fill-eoe-input dark:fill-eoe-dark-input'
                            />
                            <rect
                                x={textX}
                                y={cy + (node.hero ? 3 : 2)}
                                width={Math.max(titleWidth - 24, 24)}
                                height='4'
                                rx='2'
                                className='fill-eoe-input/70 dark:fill-eoe-dark-input/70'
                            />
                        </g>
                    )
                })}
            </svg>
        </div>
    )
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
    count = 1,
    height = 'h-80',
    width = 'w-[100%]',
    margin = '',
    padding = '',
    avatarSize = 'h-8 w-8',
    className = '',
    containerClassName = '',
    backgroundColor = '',
    rounded = '',
    variant = 'default',
    ariaLabel,
}) => {
    const avatarImage = (
        <div
            className={`
                loading-skeleton-circle-avatar-image
                ${baseSkeletonClassName}
                ${avatarSize} m-0 rounded-full ${margin}
            `}
        />
    )

    if (variant === 'circle-avatar') {
        return (
            <div
                className={`loading-skeleton-circle-avatar-list flex flex-col gap-2 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <div
                        key={`loading-skeleton-circle-avatar-${index}`}
                        className='loading-skeleton-circle-avatar-item flex flex-col items-center gap-2'
                    >
                        {avatarImage}
                        <div
                            className={`
                                loading-skeleton-circle-avatar-label
                                ${baseSkeletonClassName}
                                h-3 w-16 m-0 rounded-full ${className}
                            `}
                        />
                    </div>
                ))}
            </div>
        )
    }

    if (variant === 'entity-avatar') {
        return (
            <div
                className={`loading-skeleton-entity-avatar-list flex flex-col gap-2 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <div
                        key={`loading-skeleton-entity-avatar-${index}`}
                        className={`loading-skeleton-entity-avatar-item flex flex-row items-center gap-x-1 ${backgroundColor} ${padding} ${rounded}`}
                    >
                        {avatarImage}
                        <div className={`flex flex-col gap-x-1 gap-y-1 min-w-0 flex-1 ${margin}`}>
                            <div
                                className={`
                                    loading-skeleton-entity-avatar-title
                                    ${baseSkeletonClassName}
                                    h-3 w-24 m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-entity-avatar-subtitle
                                    ${baseSkeletonClassName}
                                    h-3 w-48 m-0 rounded-full ${className}
                                `}
                            />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    if (variant === 'entity-card') {
        return (
            <div
                className={`loading-skeleton-entity-card-list flex flex-col gap-3 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <article
                        key={`loading-skeleton-entity-card-${index}`}
                        className='loading-skeleton-entity-card-item overflow-hidden rounded-lg bg-transparent'
                    >
                        <div className='loading-skeleton-entity-card-header px-2 py-2 flex flex-col gap-y-2'>
                            <div className='loading-skeleton-entity-card-header-row flex flex-row items-center gap-x-1'>
                                <div
                                    className={`
                                        loading-skeleton-entity-card-image
                                        ${baseSkeletonClassName}
                                        h-9 w-9 m-0 rounded-full ${className}
                                    `}
                                />
                                <div className='loading-skeleton-entity-card-header-text flex flex-col gap-y-1 min-w-0 flex-1'>
                                    <div
                                        className={`
                                            loading-skeleton-entity-card-title
                                            ${baseSkeletonClassName}
                                            h-3 w-24 m-0 rounded-full ${className}
                                        `}
                                    />
                                    <div
                                        className={`
                                            loading-skeleton-entity-card-subtitle
                                            ${baseSkeletonClassName}
                                            h-3 w-48 max-w-full m-0 rounded-full ${className}
                                        `}
                                    />
                                </div>
                            </div>
                            <div
                                className={`
                                    loading-skeleton-entity-card-caption
                                    ${baseSkeletonClassName}
                                    h-3 w-24 m-0 rounded-full ${className}
                                `}
                            />
                        </div>

                        <div
                            className={`
                                loading-skeleton-entity-card-body
                                ${baseSkeletonClassName}
                                h-56 w-full rounded-none m-0 ${className}
                            `}
                        />

                        <div className='loading-skeleton-entity-card-footer px-2 py-3 flex flex-col gap-y-2'>
                            <div
                                className={`
                                    loading-skeleton-entity-card-footer-line-primary
                                    ${baseSkeletonClassName}
                                    h-3 w-full m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-entity-card-footer-line-secondary
                                    ${baseSkeletonClassName}
                                    h-3 w-full m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-entity-card-footer-line-tertiary
                                    ${baseSkeletonClassName}
                                    h-3 w-1/2 m-0 rounded-full ${className}
                                `}
                            />
                        </div>
                    </article>
                ))}
            </div>
        )
    }

    if (variant === 'issue-card') {
        return (
            <div
                className={`loading-skeleton-issue-card-list flex flex-col gap-3 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <article
                        key={`loading-skeleton-issue-card-${index}`}
                        className='loading-skeleton-issue-card-item overflow-hidden rounded-lg bg-transparent'
                    >
                        <div className='loading-skeleton-issue-card-header px-2 py-2 flex flex-col gap-y-2'>
                            <div className='loading-skeleton-issue-card-header-row flex flex-row items-center gap-x-1'>
                                <div className='loading-skeleton-issue-card-header-text flex flex-col gap-y-1 min-w-0 flex-1'>
                                    <div
                                        className={`
                                            loading-skeleton-issue-card-title
                                            ${baseSkeletonClassName}
                                            h-6 w-48 m-0 rounded-full ${className}
                                        `}
                                    />
                                </div>
                            </div>
                            <div
                                className={`
                                    loading-skeleton-issue-card-caption
                                    ${baseSkeletonClassName}
                                    h-3 w-24 m-0 rounded-full ${className}
                                `}
                            />
                        </div>

                        <div
                            className={`
                                loading-skeleton-issue-card-body
                                ${baseSkeletonClassName}
                                h-56 w-full rounded-none m-0 ${className}
                            `}
                        />

                        <div className='loading-skeleton-issue-card-footer px-2 py-3 flex flex-col gap-y-2'>
                            <div
                                className={`
                                    loading-skeleton-issue-card-footer-line-primary
                                    ${baseSkeletonClassName}
                                    h-3 w-full m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-issue-card-footer-line-secondary
                                    ${baseSkeletonClassName}
                                    h-3 w-full m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-issue-card-footer-line-tertiary
                                    ${baseSkeletonClassName}
                                    h-3 w-1/2 m-0 rounded-full ${className}
                                `}
                            />
                        </div>
                    </article>
                ))}
            </div>
        )
    }

    if (variant === 'comment-card') {
        return (
            <div
                className={`loading-skeleton-comment-card-list flex flex-col gap-3 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <article
                        key={`loading-skeleton-comment-card-${index}`}
                        className='loading-skeleton-comment-card-item overflow-hidden rounded-lg bg-transparent'
                    >
                        <div className='loading-skeleton-comment-card-header px-2 py-2 flex flex-col gap-y-1'>
                            <div className='loading-skeleton-comment-card-header-row flex flex-row items-center gap-x-1'>
                                {avatarImage}
                                <div className='loading-skeleton-comment-card-header-text flex flex-col gap-y-1 min-w-0 flex-1'>
                                    <div
                                        className={`
                                            loading-skeleton-comment-card-title
                                            ${baseSkeletonClassName}
                                            h-3 w-24 m-0 rounded-full ${className}
                                        `}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='loading-skeleton-comment-card-footer ml-7 px-2 flex flex-col gap-y-1'>
                            <div
                                className={`
                                    loading-skeleton-comment-card-footer-line-primary
                                    ${baseSkeletonClassName}
                                    h-3 w-full m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-entity-card-footer-line-secondary
                                    ${baseSkeletonClassName}
                                    h-3 w-full m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-entity-card-footer-line-tertiary
                                    ${baseSkeletonClassName}
                                    h-3 w-1/2 m-0 rounded-full ${className}
                                `}
                            />
                        </div>
                    </article>
                ))}
            </div>
        )
    }

    if (variant === 'settings') {
        return (
            <div
                className={`loading-skeleton-settings-list flex flex-col gap-2 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <div
                        key={`loading-skeleton-settings-${index}`}
                        className='loading-skeleton-settings-item flex flex-col gap-2 bg-transparent rounded-md p-2'
                    >
                        <div
                            className={`
                                loading-skeleton-settings-label
                                ${baseSkeletonClassName}
                                h-7 w-24 m-0 rounded-full ${className}
                            `}
                        />
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div
                                key={`loading-skeleton-settings-key-${index}`}
                                className='loading-skeleton-settings-item flex flex-col p-2 gap-2 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md'
                            >
                                <div
                                    className={`
                                        loading-skeleton-settings-title
                                        ${baseSkeletonClassName}
                                        h-5 w-48 m-0 rounded-full ${className}
                                    `}
                                />
                                <div
                                    className={`
                                        loading-skeleton-settings-subtitle
                                        ${baseSkeletonClassName}
                                        h-3 w-[80%] m-0 rounded-full ${className}
                                    `}
                                />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        )
    }

    if (variant === 'actor-card') {
        return (
            <div
                className={`loading-skeleton-actor-card-list flex flex-col gap-4 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <div
                        key={`loading-skeleton-actor-card-${index}`}
                        className='loading-skeleton-actor-card-item overflow-hidden rounded-lg bg-transparent'
                    >
                        <div className='loading-skeleton-actor-card-header px-2 py-2 flex flex-col gap-y-2'>
                            <div
                                className={`
                                    loading-skeleton-actor-card-title
                                    ${baseSkeletonClassName}
                                    h-6 w-32 m-0 rounded-full ${className}
                                `}
                            />
                            <div className='loading-skeleton-actor-card-header-row flex flex-row items-center gap-x-1 justify-between'>
                                {avatarImage}
                                <div className='loading-skeleton-actor-card-header-text flex flex-row items-center justify-center gap-x-1 min-w-0 flex-1'>
                                {Array.from({ length: 3 }).map((_, index) => (
                                    <div key={`loading-skeleton-actor-card-stats-${index}`} className='flex flex-col justify-center items-center gap-2'>
                                        <div
                                        className={`
                                            loading-skeleton-actor-card-subtitle
                                            ${baseSkeletonClassName}
                                            h-6 w-8 m-0 rounded-full ${className}
                                        `}
                                        />
                                        <div
                                        className={`
                                            loading-skeleton-actor-card-subtitle
                                            ${baseSkeletonClassName}
                                            h-3 w-16 m-0 rounded-full ${className}
                                        `}
                                        />
                                    </div>
                                    ))}
                                </div>
                            </div>
                            <div
                                className={`
                                    loading-skeleton-actor-card-footer
                                    ${baseSkeletonClassName}
                                    h-3 w-1/2 m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className={`
                                    loading-skeleton-actor-card-button
                                    ${baseSkeletonClassName}
                                    h-7 w-24 m-0 rounded-full ${className}
                                `}
                            />
                            <div
                                className='loading-skeleton-actor-card-details h-16 flex flex-col p-2 gap-2 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md'
                            />
                            <div
                                className='loading-skeleton-actor-card-details h-16 flex flex-col p-2 gap-2 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md'
                            />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    if (variant === 'graph') {
        return (
            <div
                className={`loading-skeleton-graph relative h-full min-h-[320px] w-full ${containerClassName}`}
            >
                <GraphCanvasSkeleton ariaLabel={ariaLabel} />
            </div>
        )
    }

    if (variant === 'graph-card') {
        const statusAttrs = ariaLabel
            ? {
                  role: 'status' as const,
                  'aria-label': ariaLabel,
                  'aria-live': 'polite' as const,
                  'aria-busy': true as const,
              }
            : {}
        return (
            <div
                {...statusAttrs}
                className={`loading-skeleton-graph-card-list flex flex-col gap-4 ${containerClassName}`}
            >
                {Array.from({ length: count }).map((_, index) => (
                    <article
                        key={`loading-skeleton-graph-card-${index}`}
                        className='loading-skeleton-graph-card-item overflow-hidden rounded-lg bg-transparent'
                    >
                        <div className='loading-skeleton-graph-card-header flex items-center gap-2 px-2 py-2'>
                            <div
                                className={`loading-skeleton-graph-card-avatar ${baseSkeletonClassName} h-9 w-9 rounded-full ${className}`}
                            />
                            <div className='flex min-w-0 flex-1 flex-col gap-1'>
                                <div
                                    className={`loading-skeleton-graph-card-username ${baseSkeletonClassName} h-3 w-24 rounded-full ${className}`}
                                />
                                <div
                                    className={`loading-skeleton-graph-card-meta ${baseSkeletonClassName} h-3 w-16 rounded-full ${className}`}
                                    style={{ animationDelay: '120ms' }}
                                />
                            </div>
                            <div
                                className={`loading-skeleton-graph-card-kebab ${baseSkeletonClassName} h-4 w-4 rounded-full ${className}`}
                                style={{ animationDelay: '220ms' }}
                            />
                        </div>
                        <div className='loading-skeleton-graph-card-chips flex items-center gap-2 px-2 py-1'>
                            <div
                                className={`loading-skeleton-graph-card-chip-a ${baseSkeletonClassName} h-6 w-20 rounded-full ${className}`}
                            />
                            <div
                                className={`loading-skeleton-graph-card-chip-b ${baseSkeletonClassName} h-6 w-16 rounded-full ${className}`}
                                style={{ animationDelay: '90ms' }}
                            />
                        </div>
                        <div className='loading-skeleton-graph-card-title px-2 py-1'>
                            <div
                                className={`loading-skeleton-graph-card-title-bar ${baseSkeletonClassName} h-5 w-1/2 rounded-full ${className}`}
                            />
                        </div>
                        <div className='loading-skeleton-graph-card-canvas px-2 pt-2'>
                            <div className='h-[50vh] min-h-[320px] w-full overflow-hidden rounded-2xl border border-eoe-border-main dark:border-eoe-dark-border-main'>
                                <GraphCanvasSkeleton />
                            </div>
                        </div>
                        <div className='loading-skeleton-graph-card-footer flex items-center gap-3 px-3 py-3'>
                            <div
                                className={`loading-skeleton-graph-card-vote ${baseSkeletonClassName} h-7 w-24 rounded-full ${className}`}
                            />
                            <div
                                className={`loading-skeleton-graph-card-comment ${baseSkeletonClassName} h-7 w-16 rounded-full ${className}`}
                                style={{ animationDelay: '100ms' }}
                            />
                            <div className='flex-1' />
                            <div
                                className={`loading-skeleton-graph-card-share ${baseSkeletonClassName} h-5 w-5 rounded-full ${className}`}
                                style={{ animationDelay: '160ms' }}
                            />
                            <div
                                className={`loading-skeleton-graph-card-save ${baseSkeletonClassName} h-5 w-5 rounded-full ${className}`}
                                style={{ animationDelay: '220ms' }}
                            />
                        </div>
                    </article>
                ))}
            </div>
        )
    }

    return (
        <div
            className={`loading-skeleton-default-list flex flex-col gap-1 ${containerClassName}`}
        >
            {Array.from({ length: count }).map((_, index) => (
                <div
                    key={index}
                    className={`
                        loading-skeleton-default-item
                        ${baseSkeletonClassName}
                        ${height} ${width} ${margin} ${className} ${rounded}
                    `}
                />
            ))}
        </div>
    )
}

export default LoadingSkeleton